import { Subscription } from '@rails/actioncable';
import {
  AiReviewStatus,
  BulkSms_Create,
  CompanyJobJobEventChannel_Read,
  deleteV1CompaniesCompanyIdLoadsId,
  getV1CompaniesCompanyIdJobs,
  getV1CompaniesCompanyIdJobsId,
  getV1CompaniesCompanyIdJobsJobIdDeviceStatus,
  getV1CompaniesCompanyIdJobsJobIdJobEvents,
  GetV1CompaniesCompanyIdJobsJobIdJobEventsData,
  getV1CompaniesCompanyIdJobsJobIdLoads,
  getV1CompaniesCompanyIdJobsJobIdLoadsCycles,
  getV1CompaniesCompanyIdJobsJobIdSignatures,
  getV1CompaniesCompanyIdLoadsLoadIdSignatures,
  getV1CompaniesCompanyIdOrdersOrderIdJobs,
  Job_Read,
  JobState,
  JobStateEvent,
  patchV1CompaniesCompanyIdJobsBulkUpdate,
  patchV1CompaniesCompanyIdJobsId,
  patchV1CompaniesCompanyIdLoadsId,
  postV1CompaniesCompanyIdJobsJobIdLoads,
  postV1CompaniesCompanyIdJobsJobIdSplit,
  putV1CompaniesCompanyIdJobsBulkDriverSms,
  putV1CompaniesCompanyIdJobsJobIdAccept,
  putV1CompaniesCompanyIdJobsJobIdApprove,
  putV1CompaniesCompanyIdJobsJobIdArrive,
  putV1CompaniesCompanyIdJobsJobIdAssignVendor,
  putV1CompaniesCompanyIdJobsJobIdCancel,
  putV1CompaniesCompanyIdJobsJobIdComplete,
  putV1CompaniesCompanyIdJobsJobIdCompleteLoad,
  putV1CompaniesCompanyIdJobsJobIdDriverSms,
  putV1CompaniesCompanyIdJobsJobIdEnroute,
  putV1CompaniesCompanyIdJobsJobIdFlag,
  putV1CompaniesCompanyIdJobsJobIdLoadMaterial,
  putV1CompaniesCompanyIdJobsJobIdPay,
  putV1CompaniesCompanyIdJobsJobIdReject,
  putV1CompaniesCompanyIdJobsJobIdRemoveFlag,
  putV1CompaniesCompanyIdJobsJobIdRemoveSignoff,
  putV1CompaniesCompanyIdJobsJobIdRequest,
  putV1CompaniesCompanyIdJobsJobIdRestore,
  putV1CompaniesCompanyIdJobsJobIdReview,
  putV1CompaniesCompanyIdJobsJobIdSignOff,
  putV1CompaniesCompanyIdJobsJobIdUnapprove,
  putV1CompaniesCompanyIdJobsJobIdUnassign,
  putV1CompaniesCompanyIdJobsJobIdUnloadMaterial,
  putV1CompaniesCompanyIdLoadsLoadIdApprove,
  putV1CompaniesCompanyIdLoadsLoadIdUnapprove,
} from '@treadinc/horizon-api-spec';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { omit } from 'lodash-es';
import { useCallback, useState } from 'react';

import { BulkEditJobDTO } from '~components/Job/BulkEditJobDialog';
import { EXPORT_ROW_LIMIT } from '~constants/consts';
import { useDataGridSearch } from '~hooks/useDataGridSearch/useDataGridSearch';
import { DeviceStatusRead } from '~hooks/useDeviceStatus/models';
import {
  Load,
  LoadCycle,
  LoadDirtyWaypoints,
  LoadDTO,
  LoadOptionsProps,
} from '~hooks/useLoad/models';
import { SignatureFactory } from '~hooks/useSignatures/models';
import { WayPoint } from '~hooks/useSites/models';
import connection from '~services/connectionModule';
import { extractPagination, Pagination, PaginationQuery } from '~services/pagination';
import { realTimeChannels } from '~services/realTimeChannels';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { downloadCsvFile } from '~utils/csv/csvDownload';
import { exportRowLimitAlert } from '~utils/csv/exportRowLimitAlert';
import { isNotFoundError } from '~utils/extractAxiosError';
import { fetchAllApiPages, TooManyRecordsError } from '~utils/fetchAllPages';
import { approvalFilterToBoolean } from '~utils/filters/approval-filter-utils';
import rethrowIfAborted from '~utils/rethrowIfAborted';
import { channelMessageData } from '~utils/rtu/rtu-utils';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import { Job, JobInvoicing, JobTripEvent } from './models';

export interface GetJobsByCompanyIdParams {
  companyId: string;
  pagination: Pagination;
  filters?: {
    aiReviewStatuses?: AiReviewStatus[];
    driverIds?: string[];
    dropoffSitesIds?: string[];
    endDate?: string;
    foremanIds?: string[];
    jobApprovalStatus?: string[];
    jobStates?: JobState[];
    loadApprovalStatus?: string[];
    materialIds?: string[];
    pickupSitesIds?: string[];
    prevailingWage?: string[];
    refinedJobStates?: JobState[];
    searchQuery?: string;
    startDate?: string;
    ticketPotentialDuplicate?: string[];
    ticketTypes?: string[];
    vendorAccountIds?: string[];
  };
}

export const useJob = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [isSendingSms, setIsSendingSms] = useState(false);

  const { jobStore, userStore, toasterStore } = useStores();
  const { addSearchHeaderQuery } = useDataGridSearch();
  const subscribeToJobEventUpdates = useCallback(
    ({
      jobId,
      onMessageCallBack,
    }: {
      jobId: string;
      onMessageCallBack: (data: JobTripEvent) => void;
    }): Subscription | undefined => {
      const consumer = connection.realTimeConnection;
      const channel = realTimeChannels.CompanyJobJobEventChannel;
      const companyId = getEffectiveUserCompanyId(userStore);

      let subscription = consumer?.subscriptions.subscriptions.find((sub) => {
        const identifier = sub.identifier;
        return identifier.includes(channel) && identifier.includes(jobId);
      });

      if (!subscription) {
        subscription = consumer?.subscriptions?.create?.(
          {
            channel: channel,
            company_id: companyId,
            job_id: jobId,
          },
          {
            connected: () => () => {},
            disconnected: () => () => {},
            received: (message: unknown) => {
              const data =
                channelMessageData<CompanyJobJobEventChannel_Read['data']>(message);
              if (!data) return;

              const jobEvent = JobTripEvent.parse(data);
              onMessageCallBack(jobEvent);
            },
          },
        );
      }

      return subscription;
    },
    [userStore],
  );

  const getJobsByCompanyId = async ({
    companyId,
    pagination,
    filters,
  }: GetJobsByCompanyIdParams) => {
    try {
      const query = {
        'page[after]': pagination.after,
        'page[before]': pagination.before,
        'page[limit]': pagination.limit,
        'filter[ai_review][statuses]': filters?.aiReviewStatuses,
        'filter[billing_approved]': approvalFilterToBoolean(filters?.jobApprovalStatus),
        'filter[driver_ids]': filters?.driverIds,
        'filter[dropoff_site_ids]': filters?.dropoffSitesIds,
        'filter[end_date]': filters?.endDate
          ? dayjs.tz(filters.endDate).endOf('day').toISOString()
          : undefined,
        'filter[foreman_ids]': filters?.foremanIds,
        'filter[load][billing_approved]': approvalFilterToBoolean(
          filters?.loadApprovalStatus,
        ),
        'filter[material_ids]': filters?.materialIds,
        'filter[pickup_site_ids]': filters?.pickupSitesIds,
        'filter[prevailing_wage]': filters?.prevailingWage?.length
          ? filters.prevailingWage[0] === 'true'
          : undefined,
        'filter[start_date]': filters?.startDate
          ? dayjs.tz(filters.startDate).startOf('day').toISOString()
          : undefined,
        'filter[states]': filters?.refinedJobStates ?? filters?.jobStates,
        'filter[ticket][potential_duplicate]': filters?.ticketPotentialDuplicate?.length
          ? filters.ticketPotentialDuplicate[0] === 'true'
          : undefined,
        'filter[default_digital_ticket_view]':
          filters?.ticketTypes?.length === 1
            ? filters.ticketTypes[0] === 'true'
            : undefined,
        'filter[vendor_account_ids]': filters?.vendorAccountIds,
        'search[datagrid]': filters?.searchQuery,
      };

      const response = await getV1CompaniesCompanyIdJobs({
        path: { 'company-id': companyId },
        query,
      });

      const data = response.data.data.map((job) => Job.parse(job));

      return { data, pagination: extractPagination(response) };
    } catch (error) {
      connection.handleRequestError(error, t('error_messages.jobs.failed_to_fetch'));

      return { data: [] };
    }
  };

  const getJobById = async (jobId: string, companyId: string, signal?: AbortSignal) => {
    try {
      setIsLoading(true);

      const response = await getV1CompaniesCompanyIdJobsId({
        path: { 'company-id': companyId, id: jobId },
        signal,
      });

      const formatted = Job.parse(response.data.data);
      jobStore.addJob(formatted);

      return formatted;
    } catch (error) {
      if (signal?.aborted) {
        throw error;
      }

      connection.handleRequestError(
        error,
        t('error_messages.jobs.failed_to_fetch_individual_job'),
      );
    } finally {
      if (!signal?.aborted) {
        setIsLoading(false);
      }
    }
  };

  const getJobsByOrder = async (
    orderId: string,
    options?: {
      filterParams?: Record<string, any>;
      searchQuery?: string;
      signal?: AbortSignal;
    },
  ) => {
    const { filterParams, searchQuery, signal } = options ?? {};
    setIsLoading(true);
    let query: PaginationQuery = { 'page[limit]': jobStore.pagination.limit };

    query = addSearchHeaderQuery({
      searchValue: searchQuery,
      filterQuery: filterParams,
      query,
    });

    // Status keeps track of the current order or job filter status, but we don't want to send it to api
    delete query['filter[status]'];

    try {
      const companyId = getEffectiveUserCompanyId(userStore);
      const response = await getV1CompaniesCompanyIdOrdersOrderIdJobs({
        path: { 'company-id': companyId, 'order-id': orderId },
        query,
        signal,
      });
      const formatted = response.data.data.map(Job.parse);
      jobStore.setJobsByOrder(orderId, formatted);
      return formatted;
    } catch (error) {
      if (signal?.aborted) {
        throw error;
      }

      connection.handleRequestError(error, t('error_messages.jobs.failed_to_fetch'));
      return [];
    } finally {
      if (!signal?.aborted) {
        setIsLoading(false);
      }
    }
  };

  const updateJob = async (
    id: string,
    data: any,
    companyId: string,
    inline?: boolean,
  ) => {
    setIsUpdating(true);
    const deparsedModel = inline
      ? Job.deparseUpdateFromInlineMode(data)
      : Job.deparseUpdate(data);
    try {
      const response = await patchV1CompaniesCompanyIdJobsId({
        path: { 'company-id': companyId, id },
        body: omit(deparsedModel, 'id'),
      });
      const formatted = Job.parse(response.data.data);
      jobStore.updateJob(formatted);
      return formatted;
    } catch (error) {
      connection.handleRequestError(error, t('error_messages.jobs.failed_to_update'));
    } finally {
      setIsUpdating(false);
    }
  };

  const doEvent = async (
    id: string,
    event: JobStateEvent,
    companyId: string,
    data?: any,
  ) => {
    setIsUpdating(true);
    const path = { 'company-id': companyId, 'job-id': id };
    const requestOptions = data ? { path, body: data } : { path };
    let response: { data: { data: Job_Read } };

    try {
      switch (event) {
        case JobStateEvent.ACCEPT:
          response = await putV1CompaniesCompanyIdJobsJobIdAccept(requestOptions);
          break;
        case JobStateEvent.ARRIVE:
          response = await putV1CompaniesCompanyIdJobsJobIdArrive(requestOptions);
          break;
        case JobStateEvent.CANCEL:
          response = await putV1CompaniesCompanyIdJobsJobIdCancel(requestOptions);
          break;
        case JobStateEvent.COMPLETE:
          response = await putV1CompaniesCompanyIdJobsJobIdComplete(requestOptions);
          break;
        case JobStateEvent.COMPLETE_LOAD:
          response = await putV1CompaniesCompanyIdJobsJobIdCompleteLoad(requestOptions);
          break;
        case JobStateEvent.ENROUTE:
          response = await putV1CompaniesCompanyIdJobsJobIdEnroute(requestOptions);
          break;
        case JobStateEvent.FLAG:
          response = await putV1CompaniesCompanyIdJobsJobIdFlag(requestOptions);
          break;
        case JobStateEvent.LOAD_MATERIAL:
          response = await putV1CompaniesCompanyIdJobsJobIdLoadMaterial(requestOptions);
          break;
        case JobStateEvent.PAY:
          response = await putV1CompaniesCompanyIdJobsJobIdPay(requestOptions);
          break;
        case JobStateEvent.REJECT:
          response = await putV1CompaniesCompanyIdJobsJobIdReject(requestOptions);
          break;
        case JobStateEvent.REMOVE_FLAG:
          response = await putV1CompaniesCompanyIdJobsJobIdRemoveFlag(requestOptions);
          break;
        case JobStateEvent.REMOVE_SIGNOFF:
          response = await putV1CompaniesCompanyIdJobsJobIdRemoveSignoff(requestOptions);
          break;
        case JobStateEvent.REQUEST:
          response = await putV1CompaniesCompanyIdJobsJobIdRequest(requestOptions);
          break;
        case JobStateEvent.REVIEW:
          response = await putV1CompaniesCompanyIdJobsJobIdReview(requestOptions);
          break;
        case JobStateEvent.SIGN_OFF:
          response = await putV1CompaniesCompanyIdJobsJobIdSignOff(requestOptions);
          break;
        case JobStateEvent.UNASSIGN:
          response = await putV1CompaniesCompanyIdJobsJobIdUnassign(requestOptions);
          break;
        case JobStateEvent.UNLOAD_MATERIAL:
          response = await putV1CompaniesCompanyIdJobsJobIdUnloadMaterial(requestOptions);
          break;
        case JobStateEvent.RESTORE:
          response = await putV1CompaniesCompanyIdJobsJobIdRestore(requestOptions);
          break;
        default:
          throw new Error(t('error_messages.jobs.failed_to_do_event', { event }));
      }

      const formatted = JobInvoicing.parse(response.data.data);
      jobStore.updateJob(formatted);
      return formatted;
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.jobs.failed_to_do_event', { event }),
      );
    } finally {
      setIsUpdating(false);
    }
  };

  const assignDriver = async (
    jobId: string,
    driverId: string,
    companyId: string,
    complianceOverride?: boolean,
  ) => {
    setIsUpdating(true);
    try {
      const response = await patchV1CompaniesCompanyIdJobsId({
        path: { 'company-id': companyId, id: jobId },
        body: {
          driver_id: driverId,
          ...(complianceOverride ? { compliance_override: true } : {}),
        },
      });
      const formatted = Job.parse(response.data.data);
      jobStore.updateJob(formatted);
      return formatted;
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.jobs.failed_to_assign_driver'),
      );
    } finally {
      setIsUpdating(false);
    }
  };

  const unassignDriver = async (jobId: string, companyId: string) => {
    setIsUpdating(true);
    try {
      const response = await patchV1CompaniesCompanyIdJobsId({
        path: { 'company-id': companyId, id: jobId },
        body: { driver_id: null },
      });
      const formatted = Job.parse(response.data.data);
      jobStore.updateJob(formatted);
      return formatted;
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.jobs.failed_to_unassign_driver'),
      );
    } finally {
      setIsUpdating(false);
    }
  };

  const assignVendor = async (
    id: string,
    vendorId: string,
    companyId: string,
    complianceOverride?: boolean,
    vendorEquipmentTypeId?: string,
  ) => {
    setIsUpdating(true);
    try {
      const response = await putV1CompaniesCompanyIdJobsJobIdAssignVendor({
        path: { 'company-id': companyId, 'job-id': id },
        body: {
          vendor_id: vendorId,
          ...(complianceOverride ? { compliance_override: true } : {}),
          ...(vendorEquipmentTypeId
            ? { vendor_equipment_type_id: vendorEquipmentTypeId }
            : {}),
        },
      });
      const formatted = Job.parse(response.data.data);
      jobStore.updateJob(formatted);
      return formatted;
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.jobs.failed_to_assign_vendor'),
      );
    } finally {
      setIsUpdating(false);
    }
  };

  const getAllJobEvents = useCallback(
    async (
      jobId: GetV1CompaniesCompanyIdJobsJobIdJobEventsData['path']['job-id'],
      companyId: GetV1CompaniesCompanyIdJobsJobIdJobEventsData['path']['company-id'],
    ) => {
      setIsLoading(true);

      try {
        return await fetchAllApiPages(
          (query) =>
            getV1CompaniesCompanyIdJobsJobIdJobEvents({
              path: { 'job-id': jobId, 'company-id': companyId },
              query,
            }),
          JobTripEvent.parse,
        );
      } catch (error) {
        return connection.handleRequestError(
          error,
          t('error_messages.jobs.failed_to_fetch_job_events'),
        );
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  const approveJob = async (jobId: string, companyId: string) => {
    setIsUpdating(true);

    try {
      const response = await putV1CompaniesCompanyIdJobsJobIdApprove({
        path: { 'job-id': jobId, 'company-id': companyId },
      });
      const formatted = JobInvoicing.parse(response.data.data);
      jobStore.updateJob(formatted);
      return formatted;
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.jobs.failed_to_approve_job'),
      );
    } finally {
      setIsUpdating(false);
    }
  };

  const unapproveJob = async (jobId: string, companyId: string) => {
    setIsUpdating(true);
    try {
      const response = await putV1CompaniesCompanyIdJobsJobIdUnapprove({
        path: { 'job-id': jobId, 'company-id': companyId },
      });
      const formatted = JobInvoicing.parse(response.data.data);
      jobStore.updateJob(formatted);
      return formatted;
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.jobs.failed_to_unapprove_job'),
      );
    } finally {
      setIsUpdating(false);
    }
  };

  const addLoad = async (jobId: string, options: LoadOptionsProps) => {
    setIsUpdating(true);
    try {
      const companyId = getEffectiveUserCompanyId(userStore);
      const response = await postV1CompaniesCompanyIdJobsJobIdLoads({
        path: { 'company-id': companyId, 'job-id': jobId },
        body: LoadDTO.deparse(options),
      });
      return LoadDTO.parse(response.data.data);
    } catch (error) {
      connection.handleRequestError(error, t('error_messages.jobs.failed_to_add_load'));
    } finally {
      setIsUpdating(false);
    }
  };

  const updateLoad = async (
    load: Load,
    originalWaypoints: WayPoint[],
    dirtyWaypoints: LoadDirtyWaypoints,
  ) => {
    try {
      setIsUpdating(true);

      const body = LoadDTO.deparseUpdate(load, originalWaypoints, dirtyWaypoints);

      const companyId = getEffectiveUserCompanyId(userStore);
      const response = await patchV1CompaniesCompanyIdLoadsId({
        path: { 'company-id': companyId, id: load.id },
        body,
      });
      const updatedLoad = LoadDTO.parse(response.data.data);

      return updatedLoad;
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.jobs.failed_to_update_job_load'),
      );

      return null;
    } finally {
      setIsUpdating(false);
    }
  };

  const removeLoad = async (loadId: string) => {
    setIsUpdating(true);

    try {
      const companyId = getEffectiveUserCompanyId(userStore);
      await deleteV1CompaniesCompanyIdLoadsId({
        path: { 'company-id': companyId, id: loadId },
      });
    } catch (error) {
      const loadAlreadyDeleted = isNotFoundError(error);

      if (loadAlreadyDeleted) {
        return;
      }

      connection.handleRequestError(
        error,
        t('error_messages.jobs.failed_to_remove_load'),
      );
    } finally {
      setIsUpdating(false);
    }
  };

  const getJobLoads = async (
    jobId: string,
    { signal }: { signal?: AbortSignal } = {},
  ) => {
    setIsLoading(true);
    const companyId = getEffectiveUserCompanyId(userStore);

    try {
      return await fetchAllApiPages(
        (query, pageSignal) =>
          getV1CompaniesCompanyIdJobsJobIdLoads({
            path: { 'company-id': companyId, 'job-id': jobId },
            query,
            signal: pageSignal,
          }),
        Load.parse,
        { signal },
      );
    } catch (error) {
      rethrowIfAborted(error, signal);

      connection.handleRequestError(
        error,
        t('error_messages.jobs.failed_to_fetch_job_loads'),
      );
      return [];
    } finally {
      if (!signal?.aborted) {
        setIsLoading(false);
      }
    }
  };

  const getSignaturesByLoadId = async ({
    loadId,
    limit = 10,
    cursor,
  }: {
    loadId: string;
    limit?: number;
    cursor?: string;
  }) => {
    const query = {
      'page[limit]': limit,
      ...(cursor && { 'page[after]': cursor }),
    };

    try {
      setIsLoading(true);
      const companyId = getEffectiveUserCompanyId(userStore);
      const response = await getV1CompaniesCompanyIdLoadsLoadIdSignatures({
        path: { 'company-id': companyId, 'load-id': loadId },
        query,
      });
      const parsedData = response.data.data.map((item) => SignatureFactory.create(item));
      const pagination = extractPagination(response);
      return { data: parsedData, pagination };
    } catch (error) {
      // A 404 here means the load was deleted out from under an open drawer
      // (e.g. removed in another tab before the RTU pruned the local cache).
      // That is expected, not an actionable error. See HORIZON-FE-2MM.
      connection.handleRequestError(
        error,
        t('error_messages.load.failed_to_fetch_signatures'),
        [404],
      );
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const getJobSignatures = useCallback(
    async (jobId: string) => {
      try {
        const companyId = getEffectiveUserCompanyId(userStore);
        const response = await getV1CompaniesCompanyIdJobsJobIdSignatures({
          path: { 'company-id': companyId, 'job-id': jobId },
        });
        return response.data.data.map((item) => SignatureFactory.create(item));
      } catch (error) {
        connection.handleRequestError(
          error,
          t('error_messages.jobs.failed_to_fetch_signatures'),
        );
        return [];
      }
    },
    [userStore],
  );

  const getJobLoadCycles = async (
    jobId: string,
    companyId = getEffectiveUserCompanyId(userStore),
  ) => {
    try {
      const response = await getV1CompaniesCompanyIdJobsJobIdLoadsCycles({
        path: { 'company-id': companyId, 'job-id': jobId },
      });
      return response.data.data.load_cycles.map((item) => LoadCycle.parse(item));
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.jobs.failed_to_fetch_load_cycles'),
      );
      return [];
    }
  };

  const approveLoad = async (loadId: string, companyId: string) => {
    setIsUpdating(true);

    try {
      const response = await putV1CompaniesCompanyIdLoadsLoadIdApprove({
        path: { 'company-id': companyId, 'load-id': loadId },
      });
      const formatted = LoadDTO.parse(response.data.data);
      return formatted;
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.jobs.failed_to_approve_load'),
      );
      throw error;
    } finally {
      setIsUpdating(false);
    }
  };

  const unapproveLoad = async (loadId: string, companyId: string) => {
    setIsUpdating(true);

    try {
      const response = await putV1CompaniesCompanyIdLoadsLoadIdUnapprove({
        path: { 'company-id': companyId, 'load-id': loadId },
      });
      const formatted = LoadDTO.parse(response.data.data);
      return formatted;
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.jobs.failed_to_unapprove_load'),
      );
      throw error;
    } finally {
      setIsUpdating(false);
    }
  };

  const sendSmsToDriver = async (jobId: string, message: string) => {
    try {
      setIsSendingSms(true);

      await putV1CompaniesCompanyIdJobsJobIdDriverSms({
        path: { 'company-id': getEffectiveUserCompanyId(userStore), 'job-id': jobId },
        body: { message },
      });
    } finally {
      setIsSendingSms(false);
    }
  };

  const bulkSendSmsToDrivers = async (args: BulkSms_Create) => {
    try {
      setIsSendingSms(true);

      await putV1CompaniesCompanyIdJobsBulkDriverSms({
        path: { 'company-id': getEffectiveUserCompanyId(userStore) },
        body: args,
      });
    } catch {
      toasterStore.push(
        alert(
          t('error_messages.jobs.failed_to_bulk_send_text_to_drivers'),
          AlertTypes.error,
        ),
      );
    } finally {
      setIsSendingSms(false);
    }
  };

  const bulkEditJob = async (jobIds: string[], data: BulkEditJobDTO) => {
    try {
      setIsUpdating(true);
      await patchV1CompaniesCompanyIdJobsBulkUpdate({
        body: Job.deparseBulkUpdate(jobIds, data),
        path: { 'company-id': getEffectiveUserCompanyId(userStore) },
      });
    } catch {
      toasterStore.push(
        alert(t('error_messages.jobs.failed_to_bulk_edit_job_notes'), AlertTypes.error),
      );
    } finally {
      setIsUpdating(false);
    }
  };

  const splitJob = async (jobId: string, companyId: string, quantities: number[]) => {
    setIsUpdating(true);
    try {
      const response = await postV1CompaniesCompanyIdJobsJobIdSplit({
        path: { 'company-id': companyId, 'job-id': jobId },
        body: { quantities },
      });
      return response.data.data.id;
    } catch (error) {
      connection.handleRequestError(error, t('error_messages.jobs.failed_to_split'));
    } finally {
      setIsUpdating(false);
    }
  };

  const subscribeToSplitJobResult = useCallback(
    (
      correlationId: string,
      onResult: (data: { success_ids: string[]; failed_ids: string[] }) => void,
    ) => {
      const consumer = connection.realTimeConnection;
      const channel = realTimeChannels.SplitJobResultChannel;

      let subscription = consumer?.subscriptions.subscriptions.find((sub) => {
        return sub.identifier.includes(channel) && sub.identifier.includes(correlationId);
      });

      if (!subscription) {
        subscription = consumer?.subscriptions?.create?.(
          { channel, id: correlationId },
          {
            received(data: { success_ids: string[]; failed_ids: string[] }) {
              onResult(data);
              this.unsubscribe();
            },
          },
        );
      }

      return subscription;
    },
    [],
  );

  const getJobDeviceStatus = async (jobId: string, companyId: string) => {
    try {
      const response = await getV1CompaniesCompanyIdJobsJobIdDeviceStatus({
        path: { 'company-id': companyId, 'job-id': jobId },
      });
      const deviceStatus = DeviceStatusRead.parse(response.data.data);
      return deviceStatus;
    } catch (error) {
      connection.handleRequestError(error);
      return null;
    }
  };

  const exportApproveJobsCsv = useCallback(
    async (filters: GetJobsByCompanyIdParams['filters']) => {
      try {
        const { transformApproveJobsToCSV } =
          await import('~utils/csv/approveJobsCsvExport');
        const companyId = getEffectiveUserCompanyId(userStore);
        // Mirrors the Approve Jobs grid, which never lists jobs nobody was assigned to.
        // The export is the billing artifact of that queue, so it carries the same rows.
        const filterQuery: Partial<PaginationQuery> = { 'filter[assigned]': true };

        if (filters?.driverIds?.length) {
          filterQuery['filter[driver_ids]'] = filters.driverIds;
        }
        if (filters?.vendorAccountIds?.length) {
          filterQuery['filter[vendor_account_ids]'] = filters.vendorAccountIds;
        }
        if (filters?.dropoffSitesIds?.length) {
          filterQuery['filter[dropoff_site_ids]'] = filters.dropoffSitesIds;
        }
        if (filters?.pickupSitesIds?.length) {
          filterQuery['filter[pickup_site_ids]'] = filters.pickupSitesIds;
        }
        if (filters?.foremanIds?.length) {
          filterQuery['filter[foreman_ids]'] = filters.foremanIds;
        }
        if (filters?.startDate) {
          filterQuery['filter[start_date]'] = dayjs
            .tz(filters.startDate)
            .startOf('day')
            .toISOString();
        }
        if (filters?.endDate) {
          filterQuery['filter[end_date]'] = dayjs
            .tz(filters.endDate)
            .endOf('day')
            .toISOString();
        }
        if (filters?.jobStates?.length) {
          filterQuery['filter[states]'] = filters.refinedJobStates ?? filters.jobStates;
        }
        if (filters?.jobApprovalStatus?.length) {
          filterQuery['filter[billing_approved]'] = approvalFilterToBoolean(
            filters.jobApprovalStatus,
          ) as unknown as string;
        }
        if (filters?.searchQuery?.trim().length) {
          filterQuery['search[datagrid]'] = filters.searchQuery.trim();
        }
        if (filters?.aiReviewStatuses?.length) {
          filterQuery['filter[ai_review][statuses]'] = filters.aiReviewStatuses;
        }
        if (filters?.loadApprovalStatus?.length) {
          filterQuery['filter[load][billing_approved]'] = approvalFilterToBoolean(
            filters.loadApprovalStatus,
          ) as unknown as string;
        }
        if (filters?.materialIds?.length) {
          filterQuery['filter[material_ids]'] = filters.materialIds;
        }
        if (filters?.prevailingWage?.length) {
          filterQuery['filter[prevailing_wage]'] = (filters.prevailingWage[0] ===
            'true') as unknown as string;
        }
        if (filters?.ticketPotentialDuplicate?.length) {
          filterQuery['filter[ticket][potential_duplicate]'] = (filters
            .ticketPotentialDuplicate[0] === 'true') as unknown as string;
        }
        if (filters?.ticketTypes?.length === 1) {
          filterQuery['filter[default_digital_ticket_view]'] = (filters.ticketTypes[0] ===
            'true') as unknown as string;
        }

        const allJobs = await fetchAllApiPages(
          (query) =>
            getV1CompaniesCompanyIdJobs({
              path: { 'company-id': companyId },
              // TODO: type filterQuery from the spec and drop this cast once the jobs
              // index accepts filter[default_digital_ticket_view]
              // https://linear.app/treadapp/issue/TRE-18358
              query: { ...filterQuery, ...query } as Parameters<
                typeof getV1CompaniesCompanyIdJobs
              >[0]['query'],
            }),
          Job.parse,
          { maxRecords: EXPORT_ROW_LIMIT },
        );

        if (allJobs.length === 0) {
          toasterStore.push(alert(t('approvals.export.no_data'), AlertTypes.info));
          return;
        }

        const csvData = transformApproveJobsToCSV(allJobs);
        const dateForFilename = dayjs().format('YYYY-MM-DD');
        const filename = `${userStore.userCompany.legalName}_approve_jobs_${dateForFilename}.csv`;
        downloadCsvFile(csvData, filename);

        toasterStore.push(
          alert(
            t('approvals.export.success', { count: allJobs.length }),
            AlertTypes.success,
          ),
        );
      } catch (error) {
        if (error instanceof TooManyRecordsError) {
          toasterStore.push(exportRowLimitAlert());

          return;
        }

        connection.handleRequestError(error, t('approvals.export.failed'));
        throw error;
      }
    },
    [toasterStore, userStore],
  );

  return {
    addLoad,
    approveJob,
    approveLoad,
    assignDriver,
    assignVendor,
    bulkEditJob,
    bulkSendSmsToDrivers,
    doEvent,
    exportApproveJobsCsv,
    getAllJobEvents,
    getJobById,
    getJobDeviceStatus,
    getJobLoadCycles,
    getJobLoads,
    getJobsByCompanyId,
    getJobsByOrder,
    getJobSignatures,
    getSignaturesByLoadId,
    isLoading,
    isSendingSms,
    isUpdating,
    removeLoad,
    sendSmsToDriver,
    splitJob,
    subscribeToSplitJobResult,
    subscribeToJobEventUpdates,
    unapproveJob,
    unapproveLoad,
    unassignDriver,
    updateJob,
    updateLoad,
  } as const;
};
